import { useId } from 'react';
import { cx } from '../../lib/cx';

export interface SegmentedControlProps<T extends string> {
  label?: string;
  /** `count` shows beside the label in lighter text ("Active 4"). */
  options: { value: T; label: string; count?: number }[];
  value: T;
  onChange: (value: T) => void;
  /** `pill` is the fully rounded, 34px version used in staff table toolbars (Figma A05.1). `compact` is the 40px track of the staff dialogs (Figma A04.1 "Add staff"). */
  shape?: 'default' | 'pill' | 'compact';
  /** For `pill`: `lg` is the 38px track of a business, project or person page (Figma A05.1); `md` the 34px one. */
  size?: 'md' | 'lg';
  className?: string;
}

/** Figma segment pill (504:35846): a single-choice radio group laid out as a track with a raised selected option. */
export function SegmentedControl<T extends string>({
  label,
  options,
  value,
  onChange,
  shape = 'default',
  size = 'md',
  className,
}: SegmentedControlProps<T>) {
  const name = useId();
  return (
    <fieldset className={cx('flex flex-col gap-(--mbm-space-2)', className)}>
      {label ? <legend className="mb-(--mbm-space-2) text-label-medium text-text-secondary">{label}</legend> : null}
      <div
        className={cx(
          shape === 'compact' || (shape === 'pill' && size === 'lg')
            ? 'flex gap-(--mbm-space-0-5) p-(--mbm-space-0-75)'
            : 'flex gap-(--mbm-space-1) p-(--mbm-space-1)',
          shape === 'pill'
            ? 'rounded-full bg-surface-field'
            : shape === 'compact'
              ? 'rounded-(--mbm-radius-sm) bg-surface-field'
              : 'rounded-(--mbm-radius-sm) bg-surface-tint',
        )}
      >
        {options.map((option) => (
          <label key={option.value} className="relative flex-1">
            <input
              type="radio"
              name={name}
              value={option.value}
              checked={value === option.value}
              onChange={() => onChange(option.value)}
              className="peer sr-only"
            />
            <span
              className={cx(
                'flex cursor-pointer items-center justify-center gap-(--mbm-space-1-5) whitespace-nowrap text-label-medium text-text-secondary',
                shape === 'pill'
                  ? size === 'lg'
                    ? 'h-[calc(var(--mbm-size-toolbar-control-lg)-var(--mbm-space-0-75)*2)] rounded-full px-(--mbm-space-3-5)'
                    : 'h-[calc(var(--mbm-size-toolbar-control)-var(--mbm-space-1)*2)] rounded-full px-(--mbm-space-3-5)'
                  : shape === 'compact'
                    ? 'h-[calc(var(--mbm-size-segmented-compact)-var(--mbm-space-0-75)*2)] rounded-(--mbm-component-radius-segment-option) px-(--mbm-space-2) whitespace-nowrap'
                    : 'h-(--mbm-size-segmented) rounded-(--mbm-radius-xs) px-(--mbm-space-2)',
                'peer-checked:bg-surface-card peer-checked:text-text-primary peer-checked:shadow-(--mbm-shadow-sm)',
                'peer-focus-visible:shadow-(--mbm-shadow-focus-ring)',
              )}
            >
              {option.label}
              {option.count !== undefined ? (
                <span className="text-body-xsmall text-text-tertiary">{option.count}</span>
              ) : null}
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}
